'use client';

import type { ReviewRun } from '@/data/reviews/use-review-statuses';
import type { CustomRule, CustomRulesResponse } from '@/lib/custom-rules/schema';
import type { InstallationRepositories } from '@/types/github';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { customRulesQueryKey } from '@/data/custom-rules/use-custom-rules';
import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import { installationRepositoriesQueryKey } from '@/data/installations/queries';
import { onboardingStatusQueryKey } from '@/data/onboarding/use-onboarding-status';
import { reviewRunsQueryKey } from '@/data/reviews/use-review-statuses';
import { TRIGGER_SOURCE } from '@/lib/ai/code-review-service/trigger-review-source';

type TriggerAiReviewResponse = {
  success: boolean;
  message: string;
  traceId: string;
};

type TriggerAiReviewErrorResponse = {
  error?: string;
};

type TriggerAiReviewMutationContext = {
  previousReviewRuns?: ReviewRun[];
  hadPreviousReviewRuns: boolean;
  queryKey?: ReturnType<typeof reviewRunsQueryKey>;
  optimisticRunIds?: string[];
};

type TriggerOptimisticRunsResult = {
  runs: ReviewRun[];
  optimisticRunIds: string[];
};

type TriggerAiReviewParams = {
  owner: string;
  repo: string;
  number: string;
  installationId: number;
  repositoryId?: number;
};

/**
 * Builds deterministic optimistic runs for a trigger request so the UI can
 * render immediately and reconcile once persisted rows materialize.
 */
function buildTriggerOptimisticRuns(
  enabledRules: CustomRule[],
  seed: number = Date.now(),
  requestedAtIso: string = new Date().toISOString(),
): TriggerOptimisticRunsResult {
  const generalOptimisticId = `optimistic-general-${seed}`;
  const runs: ReviewRun[] = [
    {
      id: generalOptimisticId,
      reviewRunId: generalOptimisticId,
      traceId: '',
      status: 'running',
      runKind: 'general',
      violationCount: 0,
      createdAt: requestedAtIso,
      updatedAt: requestedAtIso,
      isPendingOptimistic: true,
      optimisticRequestedAt: requestedAtIso,
    },
  ];

  for (const rule of enabledRules) {
    const optimisticRuleId = `optimistic-rule-${rule.id}-${seed}`;
    runs.push({
      id: optimisticRuleId,
      reviewRunId: optimisticRuleId,
      traceId: '',
      status: 'running',
      runKind: 'rule',
      ruleId: rule.id,
      ruleTitle: rule.title,
      violationCount: 0,
      createdAt: requestedAtIso,
      updatedAt: requestedAtIso,
      isPendingOptimistic: true,
      optimisticRequestedAt: requestedAtIso,
    });
  }

  return {
    runs,
    optimisticRunIds: runs.map((run) => run.reviewRunId),
  };
}

/**
 * Patches optimistic placeholder rows with backend traceId once the trigger API
 * responds, so reconciliation can deterministically match persisted runs.
 */
function attachTraceIdToOptimisticRuns(
  runs: ReviewRun[],
  optimisticRunIds: string[],
  traceId: string,
  updatedAtIso: string = new Date().toISOString(),
): ReviewRun[] {
  if (!traceId || optimisticRunIds.length === 0) {
    return runs;
  }

  const optimisticIdSet = new Set(optimisticRunIds);

  return runs.map((run) => {
    if (!run.isPendingOptimistic || !optimisticIdSet.has(run.reviewRunId)) {
      return run;
    }

    return {
      ...run,
      traceId,
      updatedAt: updatedAtIso,
    };
  });
}

async function triggerAiReview(params: TriggerAiReviewParams) {
  const { owner, repo, number, installationId, repositoryId } = params;
  const response = await fetch(
    `/api/github/repos/${owner}/${repo}/pulls/${number}/trigger-review-new`,
    {
      method: 'POST',
      body: JSON.stringify({
        installationId,
        repositoryId,
        reviewScope: { kind: 'full' },
        triggerSource: TRIGGER_SOURCE.UI_MANUAL,
      }),
    },
  );

  if (!response.ok) {
    let errorResponse: TriggerAiReviewErrorResponse | undefined;

    try {
      errorResponse = (await response.json()) as TriggerAiReviewErrorResponse;
    } catch (error) {
      console.info('Failed to parse error response as json:', error);
    }

    if (errorResponse?.error) {
      throw new Error(errorResponse.error);
    }
    throw new Error('Failed to trigger AI review');
  }

  return (await response.json()) as TriggerAiReviewResponse;
}

export function useTriggerAiReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerAiReview,
    onMutate: async (variables: TriggerAiReviewParams) => {
      const { owner, repo, number, installationId } = variables;

      // Guard – bail out early if something is missing
      if (!owner || !repo || !number) {
        console.error('[useTriggerAiReview] Missing owner, repo, or number – aborting mutation');
        throw new Error('Missing required PR parameters');
      }

      // Get repositoryId from cached installation data
      const installationKey = installationRepositoriesQueryKey(
        installationId,
        REPOSITORY_DATA_SOURCE.GITHUB,
      );
      const installationData = queryClient.getQueryData<InstallationRepositories>(installationKey);
      const repositoryId = installationData?.repositories.find(
        (r) =>
          r.name.toLowerCase() === repo.toLowerCase() &&
          r.owner?.login.toLowerCase() === owner.toLowerCase(),
      )?.id;
      variables.repositoryId = repositoryId;

      // Get enabled custom rules from cached custom rules data
      let enabledRules: CustomRule[] = [];
      if (repositoryId) {
        const rulesKey = customRulesQueryKey(installationId, repositoryId);
        const allRules = queryClient.getQueryData<CustomRulesResponse>(rulesKey)?.rules ?? [];
        enabledRules = allRules.filter((rule) => rule.enabled);
      }

      const queryKey = reviewRunsQueryKey(owner, repo, number);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousReviewRuns = queryClient.getQueryData<ReviewRun[]>(queryKey);
      const hadPreviousReviewRuns = previousReviewRuns !== undefined;

      // Optimistically add a new review run with "running" status
      // Note: This assumes a "general" review is triggered. If specific rules are involved,
      // ruleId and ruleTitle would need to be passed or determined.
      const optimisticSeed = Date.now();
      const requestedAtIso = new Date(optimisticSeed).toISOString();
      const { runs: optimisticRuns, optimisticRunIds } = buildTriggerOptimisticRuns(
        enabledRules,
        optimisticSeed,
        requestedAtIso,
      );

      queryClient.setQueryData(queryKey, (oldData: ReviewRun[] = []) => [
        ...(oldData || []),
        ...optimisticRuns,
      ]);

      return { previousReviewRuns, hadPreviousReviewRuns, queryKey, optimisticRunIds };
    },
    onSuccess: (
      data: TriggerAiReviewResponse,
      _variables: {
        owner: string;
        repo: string;
        number: string;
        installationId: number;
        repositoryId?: number;
      },
      context: TriggerAiReviewMutationContext | undefined,
    ) => {
      const traceId = data?.traceId;
      if (!traceId || !context?.queryKey || !context.optimisticRunIds?.length) {
        return;
      }

      queryClient.setQueryData<ReviewRun[]>(context.queryKey, (runs = []) =>
        attachTraceIdToOptimisticRuns(runs, context.optimisticRunIds ?? [], traceId),
      );

      // The optimistic update will be reconciled by useReviewRuns polling or realtime updates.
      // Optionally, one could invalidate the query here to force a refetch sooner:
      // queryClient.invalidateQueries({ queryKey: reviewRunsQueryKey(variables.owner, variables.repo, variables.number) });
    },
    onError: (
      error: Error,
      _variables: {
        owner: string;
        repo: string;
        number: string;
        installationId: number;
        repositoryId?: number;
      },
      context: TriggerAiReviewMutationContext | undefined,
    ) => {
      console.error('Error triggering AI review:', error);

      if (error.message === 'Subscription missing') {
        console.error('Subscription missing error');
        toast.error(
          'Your subscription is currently inactive. Please reactivate your subscription to run AI reviews.',
        );
      } else {
        toast.error(error.message || 'Failed to start AI review');
      }
      // If the mutation fails, roll back to the previous state.
      if (!context?.queryKey) {
        return;
      }

      if (context.hadPreviousReviewRuns) {
        queryClient.setQueryData(context.queryKey, context.previousReviewRuns);
        return;
      }

      queryClient.removeQueries({ queryKey: context.queryKey, exact: true });
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate onboarding status when a review is triggered
      if (variables?.installationId) {
        queryClient.invalidateQueries({
          queryKey: onboardingStatusQueryKey(variables.installationId),
        });
      }
    },
  });
}
